import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Satellite, Cloud, Server, Globe2, MapPin } from "lucide-react";
import { SECTION } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

const ICONS = [ShieldCheck, Satellite, Cloud, Server, Globe2];

export default function CaseStudies() {
  const { t } = useI18n();
  const cs = t.cases;
  const [active, setActive] = useState(0);
  const c = cs.list[active];
  const Icon = ICONS[active] || ShieldCheck;

  return (
    <section
      id="references"
      data-testid={SECTION.cases}
      className="section-y bg-white relative overflow-hidden"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="text-overline">{cs.overline}</span>
          <h2 className="mt-4 font-display font-semibold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
            {cs.title}
          </h2>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {cs.list.map((item, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`case-tab-${i}`}
              className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${
                active === i
                  ? "bg-navy text-white border-navy shadow-md"
                  : "bg-white text-slate-700 border-slate-200 hover:border-navy/40"
              }`}
            >
              {item.tag}
            </button>
          ))}
        </div>

        <motion.div
          key={`${active}-${cs.overline}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-10"
        >
          <div className="lg:col-span-5 p-8 lg:p-10 rounded-3xl bg-navy text-white relative overflow-hidden">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-cyan-tech/15 blur-3xl" />
            <div className="absolute inset-0 opacity-[0.06] noise-bg" />
            <div className="relative">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-tech/15 text-cyan-tech">
                <Icon className="h-6 w-6" />
              </div>
              <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-cyan-tech">
                {c.tag}
              </div>
              <h3 className="mt-3 font-display text-2xl lg:text-3xl font-semibold leading-tight">
                {c.title}
              </h3>
              <div className="mt-8 inline-flex items-baseline gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                <span className="font-display text-3xl font-semibold text-gold">
                  {c.metric.value}
                </span>
                <span className="text-sm text-slate-300">{c.metric.label}</span>
              </div>
              {c.countries && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {c.countries.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white"
                    >
                      <MapPin className="h-3 w-3 text-cyan-tech" /> {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Block title={cs.labels.context} content={c.context} />
            <Block title={cs.labels.actions} list={c.actions} />
            <Block title={cs.labels.results} list={c.results} highlight />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Block({ title, content, list, highlight }) {
  return (
    <div
      className={`p-6 rounded-2xl border ${
        highlight ? "border-gold/40 bg-amber-50/40" : "border-slate-200 bg-white"
      }`}
    >
      <div className="text-overline">{title}</div>
      {content && <p className="mt-3 text-sm text-slate-700 leading-relaxed">{content}</p>}
      {list && (
        <ul className="mt-3 space-y-2">
          {list.map((it) => (
            <li
              key={it}
              className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed"
            >
              <span
                className={`mt-1.5 inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                  highlight ? "bg-gold" : "bg-cyan-700"
                }`}
              />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
